import type { ReactNode } from 'react'
import { useLocation } from 'react-router-dom'
import { SmartLink } from './SmartLink'
import type { LinkTarget } from './types'

/**
 * One tab.
 *
 * It extends `LinkTarget`, so a tab is an in-app route (`to`), another KKF app (`href`), or
 * — with neither — a button that runs `onClick`. Partners' fifth tab is that third
 * case: it opens the off-canvas rather than going anywhere, and a link to nowhere with a
 * click handler on it is a link a middle-click opens in a blank tab.
 */
export interface BottomNavItem extends LinkTarget {
  /** The flaticon suffix — `location`, `search`, `heart`. Ignored when `node` is given. */
  icon?: string
  /**
   * A custom glyph, for a tab whose icon carries state. Partners' saved tab is a
   * heart that fills once something is saved, which no static icon name can express.
   */
  node?: ReactNode
  label: string
  /** Neither `to` nor `href`: the tab is a `<button>` and this is what it does. */
  onClick?: () => void
  /**
   * The one filled tab. At most one per bar — it is the single action this app exists for,
   * and a row of five emphasised tabs emphasises nothing.
   */
  primary?: boolean
  /** A count over the glyph. Rendered only above zero, so "0 saved" is not a badge. */
  badge?: number
  /** Marks this tab current for any route beneath it, not only an exact match. */
  matchPrefix?: boolean
}

export interface BottomNavProps {
  items: BottomNavItem[]
  /**
   * The bar's accessible name — `ul.bottomNav.label` in the catalogue, resolved by the
   * caller. It is required rather than defaulted: a default would be English, and an
   * English landmark name inside a Mongolian page is exactly the failure ground rule 8
   * describes. Two `<nav>` landmarks with no names are also indistinguishable in a screen
   * reader's landmark list, and every app that mounts this already has a header nav.
   */
  label: string
}

/**
 * The fixed bar under the thumb, below 992px.
 *
 * ## Why it is here rather than in an app
 *
 * It was `.ul-mkt-tabbar`, app-local to `app_partners`, and correctly so while one app had
 * one. Ground rule 5 promotes a shared block when a **second** app needs it, and on
 * 2026-09-19 four more did. So there is one definition, and Partners now renders this
 * rather than its own copy.
 *
 * Note what did NOT get promoted: `MarketShell`, `AdminShell` and `AppShell` are still
 * three shells. A tab bar is a component; a shell is a shape, and `MarketShell`'s own
 * header records that a *third* app arguing for its own shell would be the signal to unify
 * them. This is the component coming out of the second one, not the shells merging.
 *
 * ## What the caller still owes it
 *
 * **Room at the bottom of the page.** The bar is `position: fixed`, so it sits on top of
 * whatever the last section of the page is. Every shell that mounts this must give its
 * `<main>` the `.ul-has-bottom-nav` class, which reserves the bar's height plus the
 * device's home indicator below 992px. It is a class rather than something this component
 * can do for itself: the element that scrolls belongs to the shell.
 *
 * ## Hidden by CSS, not unmounted
 *
 * Above 992px the bar is `display: none` rather than absent from the tree. One definition
 * of the tabs, one render path, and nothing to keep in step with `useIsMobileNav` — which
 * is the same boundary, read from the same breakpoint mixin.
 */
export function BottomNav({ items, label }: BottomNavProps) {
  const { pathname } = useLocation()

  const current = (item: BottomNavItem) => {
    if (!item.to) return undefined
    if (item.to === '/') return pathname === '/' ? ('page' as const) : undefined
    const hit = item.matchPrefix ? pathname.startsWith(item.to) : pathname === item.to
    return hit ? ('page' as const) : undefined
  }

  return (
    <nav className="ul-bottom-nav" aria-label={label}>
      {items.map((item) => {
        const body = (
          <>
            <span className="ul-bottom-nav-item-glyph">
              {item.node ?? <i className={`flaticon-${item.icon}`} aria-hidden="true" />}
              {/* `aria-hidden` on the count: the label beside it is what a screen reader
                  reads, and "3" announced on its own after "Saved" is noise. An app that
                  needs the number announced should put it in the label. */}
              {item.badge !== undefined && item.badge > 0 && (
                <span className="ul-bottom-nav-item-badge" aria-hidden="true">
                  {item.badge > 99 ? '99+' : item.badge}
                </span>
              )}
            </span>
            {item.label}
          </>
        )

        // A tab with somewhere to go is a link, whichever kind — `SmartLink` is what knows
        // whether that is a router `Link` or a plain `<a>` on another app's host.
        if (item.to || item.href) {
          return (
            <SmartLink
              key={item.label}
              to={item.to}
              href={item.href}
              // Written out at both call sites rather than hoisted into a `const`:
              // `audit:reuse` reads class names out of `className` literals in the JSX, and
              // a computed one it cannot see registers as zero definitions of the block —
              // which is exactly the "promoted but the old copy is still there" case the
              // check exists to catch, reported backwards.
              className={item.primary ? 'ul-bottom-nav-item ul-bottom-nav-item--primary' : 'ul-bottom-nav-item'}
              aria-current={current(item)}
            >
              {body}
            </SmartLink>
          )
        }

        return (
          <button
            key={item.label}
            type="button"
            className={item.primary ? 'ul-bottom-nav-item ul-bottom-nav-item--primary' : 'ul-bottom-nav-item'}
            onClick={item.onClick}
          >
            {body}
          </button>
        )
      })}
    </nav>
  )
}
